import * as React from 'react';
import { Avatar, Typography, makeStyles, Theme, createStyles, Box } from '@material-ui/core';
import {
  DailyReportComment,
  DailyReportCommentModel,
} from '../../../models/DailyReport/DailyReportCommentModel';
import ReadStatusButton from '../ReadStatusButton';
import { useEffect, useState } from 'react';
import { LocalStorageService } from '../../../services/LocalStorageService.';
import { baseURL } from '../../../constants/Setting';
import DeleteButton from '../DeleteButton';
import { DailyReportsCommentContext } from './DailyReportCommentList';
import NotificationToast from '../NotificationToast';
import { CommonStamp } from '../../../constants/Stamp';
// Assuming ReadStatusButton is in the same directory

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    comment: {
      display: 'flex',
      alignItems: 'flex-start',
      padding: theme.spacing(1),
      marginBottom: theme.spacing(0.5),
      borderRadius: theme.shape.borderRadius, // 角を丸める
      backgroundColor: '#e3f2fd', // コメント全体の背景色
    },
    commentText: {
      backgroundColor: 'white', // 本文エリアを白にする
      padding: theme.spacing(1),
      borderRadius: theme.shape.borderRadius, // 角を丸める
      flex: 1,
    },
    avatar: {
      width: theme.spacing(5),
      height: theme.spacing(5),
      marginRight: theme.spacing(2),
    },
    timestamp: {
      fontSize: '0.8rem',
      color: theme.palette.text.secondary,
      marginTop: theme.spacing(0.5),
      marginBottom: theme.spacing(0.5),
    },
    speechBubble: {
      position: 'relative',
      backgroundColor: '#fafafa',
      padding: '2px 5px',
      borderRadius: '10px',
      fontWeight: 'bold',
      fontSize: '1.5rem',
      // border: '0.5px solid #333',
      '&:before': {
        content: '""',
        position: 'absolute',
        top: '50%',
        left: '-10px',
        transform: 'translateY(-50%)',
        borderStyle: 'solid',
        borderWidth: '10px 10px 10px 0',
        borderColor: 'transparent #fafafa transparent transparent',
      },
    },
  })
);

// アイコン背景色をランダムに生成する関数
const generateAvatarColor = (id: number): string => {
  const colors = ['#bbdefb', '#f8bbd0', '#fff59d', '#c8e6c9', '#ffcdd2'];
  return colors[id % colors.length];
};

// コメントの改行を反映させる関数
const formatCommentText = (text: string | null) => {
  if (!text) return '';
  return text.split('\n').map((line, index) => (
    <React.Fragment key={index}>
      {line}
      <br />
    </React.Fragment>
  ));
};

interface DailyReportCommentProps {
  dailyReportComment: DailyReportComment;
  onReadStatusChange: () => void;
  onCommentDelete: (id: number) => void;
}

const DailyReportCommentItem = (props: DailyReportCommentProps) => {
  const { dailyReportComment, onReadStatusChange } = props;
  const [isReading, setIsReading] = useState(false);
  const [isRead, setIsRead] = useState(dailyReportComment.is_read ?? false);
  const classes = useStyles();

  const myUser = LocalStorageService.getUser();

  // ユーザーの名前の最初の文字を取得
  const initial = dailyReportComment?.user?.name ? dailyReportComment.user.name.charAt(0) : '?';

  //自分自身のコメントかのフラグ
  const isMyComment = dailyReportComment.user_id === myUser?.id;

  const handleDelete = async () => {
    try {
      if (dailyReportComment.id === null) {
        return;
      }
      await DailyReportCommentModel.delete(dailyReportComment);
      props.onCommentDelete(dailyReportComment.id);

      NotificationToast().deleted();
    } catch (error) {
      console.error('Failed to mark as read:', error);
      NotificationToast().error('削除に失敗しました');
    }
  };

  /**
   * 既読の設定を行う
   */
  const handleMarkAsRead = async () => {
    try {
      setIsReading(true);
      await DailyReportCommentModel.markAsRead(dailyReportComment);
      // await DailyReportReadModel.markAsRead(dailyReport);

      setIsRead(true);

      onReadStatusChange();
    } catch (error) {
      console.error('Failed to mark as read:', error);
      // setSnackbar({
      //   open: true,
      //   message: '既読の設定に失敗しました',
      //   severity: 'error',
      // });
    } finally {
      setIsReading(false);
    }
  };

  /** スタンプのサイズ設定（元） */
  // const stampSize = dailyReportComment?.stamp?.id == CommonStamp.Good.id ? '45px' : '65px';
  const stampSize = dailyReportComment?.stamp?.id == CommonStamp.Good.id ? '25px' : '35px';

  useEffect(() => {
    setIsRead(dailyReportComment.is_read ?? false);
  }, [dailyReportComment.is_read]);

  return (
    <div className={classes.comment}>
      {/* アバターの背景色をIDに基づいてランダム生成 */}
      <Avatar
        className={classes.avatar}
        style={{
          backgroundColor: generateAvatarColor(dailyReportComment.user_id),
        }}
      >
        {initial}
      </Avatar>
      <Box
        className={classes.commentText}
        style={{
          backgroundColor: isMyComment ? '#f7eaeb' : isRead ? '#dcdcdc' : 'white',
        }}
      >
        <Typography variant="body2">
          <strong>
            {dailyReportComment.user?.name} ({dailyReportComment.user?.email})
          </strong>
        </Typography>
        {/* 投稿日時の表示 */}
        <Typography className={classes.timestamp}>
          {new Date(dailyReportComment.created_at || '').toLocaleString('ja-JP', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </Typography>
        <Box
          display="flex"
          style={{
            gap: dailyReportComment?.stamp ? '10px' : '0px',
            justifyContent: 'space-between',
            alignItems: dailyReportComment?.stamp ? 'center' : 'unset',
            flexDirection: dailyReportComment?.stamp ? 'row' : 'column',
          }}
        >
          {dailyReportComment?.comment && (
            <Typography variant="body1">
              {formatCommentText(dailyReportComment?.comment)}
            </Typography>
          )}
          {dailyReportComment?.stamp_id && !dailyReportComment?.stamp && (
            <Typography
              variant="caption"
              style={{ color: '#a52a2a', marginTop: '5px', fontStyle: 'italic' }}
            >
              😢 このスタンプは現在利用できません
            </Typography>
          )}
          {dailyReportComment?.stamp?.path && (
            <Box display="flex" alignItems="center" style={{ marginTop: '1px' }}>
              <img
                src={baseURL({ isPublic: true }) + dailyReportComment.stamp?.path}
                alt={baseURL({ isPublic: true }) + dailyReportComment.stamp?.name}
                style={{ width: stampSize, height: stampSize, marginRight: '10px' }}
              />
              <Typography
                variant="body2"
                className={classes.speechBubble}
                style={{
                  marginTop: '5px',
                  padding: '2px 5px',
                  borderRadius: '10px',
                  fontWeight: 'bold',
                  fontSize: '1.0rem',
                  boxShadow: '0 3px 6px rgba(0, 0, 0, 0.2)',
                }}
              >
                {dailyReportComment.stamp?.name}
              </Typography>
            </Box>
          )}

          {!isMyComment ? (
            <Box display="flex" justifyContent="flex-end" style={{ marginTop: '1px' }}>
              <ReadStatusButton isReading={isReading} isRead={isRead} onClick={handleMarkAsRead} />
            </Box>
          ) : (
            <Box display="flex" justifyContent="flex-end" style={{ marginTop: '1px' }}>
              <DeleteButton onDelete={handleDelete} />
            </Box>
          )}
        </Box>
      </Box>
    </div>
  );
};

export default DailyReportCommentItem;
